import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, ArrowRight, Shield, Globe, Users, Package,
  Cpu, Zap, Settings, Leaf, Building2, Car, Scissors, FlaskConical,
  CheckCircle, Star, Clock, MapPin, ChevronRight, TrendingUp,
  FileText, Send, Award, BarChart3, Eye, Lock, Activity, Flame,
  ShoppingCart, Store, Handshake, X
} from 'lucide-react';
import { productsApi, rfqsApi, suppliersApi, activityApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isPaidUser } from '../utils/membershipUtils';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('products');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestRfqs, setLatestRfqs] = useState([]);
  const [featuredSuppliers, setFeaturedSuppliers] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuyerPrompt, setShowBuyerPrompt] = useState(false);

  // Show buyer action prompt after 5s for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => setShowBuyerPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, rfqsRes, suppliersRes, activityRes] = await Promise.all([
          productsApi.getAll({ limit: 8 }),
          rfqsApi.getAll({ limit: 9 }),
          suppliersApi.getAll({ limit: 4, verified: true }),
          activityApi.getLive().catch(() => ({ data: null }))
        ]);
        setFeaturedProducts(productsRes.data.products || []);
        setLatestRfqs(rfqsRes.data.rfqs || []);
        setFeaturedSuppliers(suppliersRes.data.suppliers || []);
        if (activityRes.data) setLiveStats(activityRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Refresh live stats every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await activityApi.getLive();
        if (res.data) setLiveStats(res.data);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
    }
  };

  const categories = [
    { id: 'electronics', name: 'Electronics', icon: Cpu, count: '45K+' },
    { id: 'generators', name: 'Generators & Parts', icon: Zap, count: '12K+' },
    { id: 'machinery', name: 'Industrial Machinery', icon: Settings, count: '38K+' },
    { id: 'agriculture', name: 'Agriculture & Food', icon: Leaf, count: '67K+' },
    { id: 'construction', name: 'Construction', icon: Building2, count: '29K+' },
    { id: 'automotive', name: 'Automotive Parts', icon: Car, count: '53K+' },
    { id: 'textiles', name: 'Textiles & Fabrics', icon: Scissors, count: '41K+' },
    { id: 'chemicals', name: 'Chemicals', icon: FlaskConical, count: '18K+' },
  ];

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'recently';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* ===================== HERO SECTION ===================== */}
      <section className="relative min-h-[600px] lg:min-h-[680px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/24228b54-c255-4ed7-b030-b5b4822b2c1e/images/00ab4eb5176e3674d2b1bef0d6a9347c2aa642a0a92852037ffee22fc6a73bb3.png')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#EA580C] mb-4 animate-fade-in">
              Global B2B Marketplace
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter mb-6 animate-fade-in stagger-1 leading-[1.08]">
              Tell Us What<br />
              You Need.<br />
              <span className="text-[#EA580C]">Get Quotes Fast.</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-300 mb-10 max-w-xl animate-fade-in stagger-2 leading-relaxed">
              Post your buy requirement and receive competitive quotes from verified suppliers worldwide.
              Industrial electronics, machinery, spare parts & more.
            </p>

            {/* Search Bar */}
            <div className="bg-white/95 backdrop-blur-sm rounded-sm p-1.5 max-w-2xl animate-slide-up stagger-3 shadow-xl">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-1.5">
                <div className="flex-1 flex items-center gap-2 bg-white rounded-sm px-4 border border-slate-100">
                  <Search className="w-5 h-5 text-slate-400 shrink-0" />
                  <Input
                    type="text"
                    placeholder="What are you looking for? e.g. Power Diodes, Generator Parts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 focus-visible:ring-0 text-slate-900 placeholder:text-slate-400 h-12"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white whitespace-nowrap px-8 h-12 rounded-sm font-semibold"
                  data-testid="hero-search-btn"
                >
                  Search
                </Button>
              </form>
            </div>

            {/* Primary CTA */}
            <div className="flex flex-wrap gap-3 mt-6 animate-fade-in stagger-4">
              <Button
                onClick={() => navigate('/rfqs/create')}
                className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-12 px-8 font-semibold text-base animate-pulse-glow"
                data-testid="hero-post-rfq-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Post Your Requirement
              </Button>
              <Button
                onClick={() => navigate('/register?type=supplier')}
                variant="outline"
                className="bg-white/10 text-white border-white/30 hover:bg-white/20 rounded-sm h-12 px-6 font-semibold"
                data-testid="hero-become-supplier-btn"
              >
                Become a Supplier
              </Button>
            </div>

            {/* Live Activity */}
            {liveStats && (
              <div className="flex items-center gap-6 mt-6 animate-fade-in stagger-5">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="font-semibold text-white">{liveStats.stats?.buyers_searching_now}</span> buyers searching now
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Activity className="w-4 h-4 text-[#EA580C]" />
                  <span className="font-semibold text-white">{liveStats.stats?.active_rfqs}</span> active RFQs
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===================== LIVE RFQ TICKER ===================== */}
      <div className="bg-[#0A192F] py-3 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-4">
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#EA580C] whitespace-nowrap shrink-0 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
            Live RFQs
          </span>
          <Marquee speed={40} gradient={false} pauseOnHover>
            {(latestRfqs.length > 0 ? latestRfqs : [
              { title: 'Power Diodes 1000V', buyer_country: 'USA', quantity: 5000, quantity_unit: 'pcs' },
              { title: 'Generator AVR Units', buyer_country: 'Germany', quantity: 200, quantity_unit: 'sets' },
              { title: 'HSS Cutting Blades', buyer_country: 'South Africa', quantity: 100, quantity_unit: 'pcs' }
            ]).map((rfq, i) => (
              <span key={i} className="text-sm text-slate-300 mx-8 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white font-medium">Buyer from {rfq.buyer_country || rfq.destination_country || 'International'}</span>
                {' needs '}
                <span className="text-[#EA580C] font-medium">{rfq.quantity} {rfq.quantity_unit}</span>
                {' of '}
                <span className="text-white">{rfq.title || rfq.product_name}</span>
              </span>
            ))}
          </Marquee>
        </div>
      </div>

      {/* ===================== 3 ENTRY PATHS ===================== */}
      <section className="py-10 lg:py-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-2">Get Started</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">How Do You Want to Trade?</h2>
            <p className="text-sm text-slate-500 mt-2">Choose your path and start connecting with global partners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Buyer Path */}
            <div
              onClick={() => navigate(isAuthenticated ? '/rfqs/create' : '/register?type=buyer')}
              className="group relative bg-white border-2 border-slate-200 hover:border-[#EA580C] p-8 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              data-testid="entry-path-buyer"
            >
              <div className="w-14 h-14 bg-[#EA580C]/10 rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#EA580C] transition-colors">
                <ShoppingCart className="w-7 h-7 text-[#EA580C] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0A192F] mb-2">I am a Buyer</h3>
              <p className="text-sm text-slate-500 mb-5 leading-relaxed">Post your requirement and receive quotes from verified suppliers worldwide.</p>
              <div className="flex items-center text-[#EA580C] font-semibold text-sm group-hover:gap-2 transition-all">
                Post Your Requirement <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
            {/* Supplier Path */}
            <div
              onClick={() => navigate(isAuthenticated ? '/dashboard/supplier' : '/register?type=supplier')}
              className="group relative bg-white border-2 border-slate-200 hover:border-[#0A192F] p-8 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              data-testid="entry-path-supplier"
            >
              <div className="w-14 h-14 bg-[#0A192F]/10 rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#0A192F] transition-colors">
                <Store className="w-7 h-7 text-[#0A192F] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0A192F] mb-2">I am a Supplier</h3>
              <p className="text-sm text-slate-500 mb-5 leading-relaxed">List your products and receive inquiries from global buyers looking for your goods.</p>
              <div className="flex items-center text-[#0A192F] font-semibold text-sm group-hover:gap-2 transition-all">
                Start Selling <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
            {/* Agent Path */}
            <div
              onClick={() => navigate(isAuthenticated ? '/dashboard/agent' : '/register?type=agent')}
              className="group relative bg-white border-2 border-slate-200 hover:border-purple-600 p-8 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              data-testid="entry-path-agent"
            >
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-purple-600 transition-colors">
                <Handshake className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0A192F] mb-2">I am an Agent</h3>
              <p className="text-sm text-slate-500 mb-5 leading-relaxed">Represent suppliers and connect them with buyers. Earn commissions on deals you facilitate.</p>
              <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all">
                Join as Agent <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BUYER ACTION PROMPT BANNER ===================== */}
      {!isAuthenticated && (
        <section className="bg-gradient-to-r from-[#EA580C] to-[#C2410C] py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-white">
              <FileText className="w-5 h-5 shrink-0" />
              <p className="text-sm font-semibold">Need products? Post your requirement now and get free quotes from verified suppliers!</p>
            </div>
            <Button
              onClick={() => navigate('/register?type=buyer')}
              className="bg-white text-[#EA580C] hover:bg-slate-50 rounded-sm h-9 px-6 text-sm font-bold shrink-0"
              data-testid="buyer-action-banner-btn"
            >
              Get Free Quotes
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </section>
      )}

      {/* ===================== LIVE BUY OFFERS (RFQ AS MAIN) ===================== */}
      <section className="py-14 lg:py-20 bg-white" id="rfqs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4 text-[#EA580C]" />
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C]">Live Buy Requirements</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
                Buyers Need These Products Now
              </h2>
              <p className="text-sm text-slate-500 mt-1">Respond to active requirements and win new business</p>
            </div>
            <div className="hidden sm:flex gap-3">
              <Button
                onClick={() => navigate('/rfqs/create')}
                className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm font-semibold"
                data-testid="rfqs-post-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Post Requirement
              </Button>
              <Button
                onClick={() => navigate('/rfqs')}
                variant="outline"
                className="border-slate-300 text-slate-700 rounded-sm"
                data-testid="view-all-rfqs-btn"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-slate-50 border border-slate-100 p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {latestRfqs.map((rfq) => (
                <div
                  key={rfq.rfq_id}
                  className="group bg-white border border-slate-200 hover:border-[#0A192F]/20 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                  data-testid={`rfq-card-${rfq.rfq_id}`}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <Link to={`/rfqs/${rfq.rfq_id}`} className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#0A192F] text-sm leading-snug line-clamp-2 group-hover:text-[#EA580C] transition-colors">
                          {rfq.title}
                        </h3>
                      </Link>
                      <div className="flex gap-1.5 ml-2 shrink-0">
                        {rfq.is_urgent && (
                          <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">Urgent</Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      <span>Buyer from {rfq.buyer_country || rfq.destination_country}</span>
                      <span className="text-slate-300">|</span>
                      <Clock className="w-3 h-3" />
                      <span>{getTimeAgo(rfq.created_at)}</span>
                    </div>

                    <p className="text-xs text-slate-500 line-clamp-2 mb-4 leading-relaxed">{rfq.description}</p>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                      <span className="text-[10px] bg-[#0A192F] text-white px-2 py-0.5 font-medium">
                        {rfq.quantity} {rfq.quantity_unit}
                      </span>
                      <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 font-medium">
                        {rfq.category}
                      </span>
                      {rfq.responses_count > 0 && (
                        <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 font-medium">
                          {rfq.responses_count} quotes
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Send Quote CTA */}
                  <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between">
                    <Link to={`/rfqs/${rfq.rfq_id}`} className="text-xs text-slate-500 hover:text-[#0A192F] font-medium flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> View Details
                    </Link>
                    <Button
                      onClick={() => {
                        if (!isAuthenticated) { navigate('/login'); return; }
                        navigate(`/rfqs/${rfq.rfq_id}`);
                      }}
                      size="sm"
                      className="bg-[#0A192F] hover:bg-[#172A46] text-white rounded-sm h-8 px-4 text-xs font-semibold"
                      data-testid={`rfq-send-quote-${rfq.rfq_id}`}
                    >
                      <Send className="w-3 h-3 mr-1.5" />
                      Send Quote
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile CTA */}
          <div className="flex sm:hidden gap-3 mt-6">
            <Button onClick={() => navigate('/rfqs/create')} className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] rounded-sm">Post Requirement</Button>
            <Button onClick={() => navigate('/rfqs')} variant="outline" className="flex-1 rounded-sm">View All</Button>
          </div>
        </div>
      </section>

      {/* ===================== STATS + ACTIVITY BAR ===================== */}
      <section className="bg-[#0A192F] py-10 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 lg:gap-8">
            {[
              { label: 'Active Suppliers', value: liveStats?.stats?.total_suppliers || '12,000+', icon: Users },
              { label: 'Products Listed', value: liveStats?.stats?.total_products || '500,000+', icon: Package },
              { label: 'Active Buy Offers', value: liveStats?.stats?.active_rfqs || '3,000+', icon: FileText },
              { label: 'Buyers Searching Now', value: liveStats?.stats?.buyers_searching_now || '48', icon: Activity, live: true },
              { label: 'Countries Served', value: '150+', icon: Globe },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-5 h-5 text-[#EA580C] mx-auto mb-2" />
                <div className="text-2xl lg:text-3xl font-bold text-white mb-0.5 tracking-tight flex items-center justify-center gap-1.5">
                  {stat.value}
                  {stat.live && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
                </div>
                <div className="text-xs text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== MID-PAGE RFQ FORM ===================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-3">RFQ System</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-6">
                Let the Right Supplier<br />Find You
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Post your buy requirement and receive competitive quotes from verified global suppliers.
                Our intelligent matching system connects you with the most relevant trade partners.
              </p>
              <div className="space-y-5">
                {[
                  { icon: Send, title: 'Fast Responses', desc: 'Get quotes from premium suppliers within hours' },
                  { icon: BarChart3, title: 'Multiple Quotes', desc: 'One request, multiple competitive offers to compare' },
                  { icon: Globe, title: 'Global Reach', desc: 'Connect with verified suppliers from 150+ countries' },
                  { icon: Award, title: 'AI-Powered Matching', desc: 'Smart matching connects your requirement to ideal suppliers' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0A192F] rounded-sm flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0A192F] text-sm mb-0.5">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-8 shadow-sm">
              <h3 className="font-bold text-lg text-[#0A192F] mb-1">Get Free Quotes</h3>
              <p className="text-xs text-slate-500 mb-6">Describe what you need and let suppliers come to you</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Product / Service *</label>
                  <input
                    type="text"
                    placeholder="e.g. Power Rectifier Diodes 1000V"
                    className="input-field"
                    data-testid="rfq-form-product"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Quantity</label>
                    <input
                      type="text"
                      placeholder="e.g. 5000"
                      className="input-field"
                      data-testid="rfq-form-quantity"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Unit</label>
                    <select className="input-field" data-testid="rfq-form-unit">
                      <option value="pieces">Pieces</option>
                      <option value="sets">Sets</option>
                      <option value="kg">Kilograms</option>
                      <option value="tons">Tons</option>
                      <option value="meters">Meters</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Requirements Details</label>
                  <textarea
                    placeholder="Describe specifications, certifications, delivery requirements..."
                    rows={3}
                    className="input-field resize-none"
                    data-testid="rfq-form-details"
                  />
                </div>
                <Button
                  onClick={() => navigate('/rfqs/create')}
                  className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-12 font-semibold"
                  data-testid="rfq-form-submit"
                >
                  Submit Requirement
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-[10px] text-slate-400 text-center">
                  Free to use. Your info is shared only with matching suppliers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CATEGORIES ===================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-2">Browse Industries</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">Source by Category</h2>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/products')}
              className="hidden sm:flex border-slate-300 text-slate-700 rounded-sm"
              data-testid="view-all-categories-btn"
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/products?industry=${cat.id}`}
                className="group bg-white border border-slate-200 p-4 text-center hover:border-[#0A192F]/20 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                data-testid={`category-${cat.id}`}
              >
                <cat.icon className="w-7 h-7 text-[#0A192F] mx-auto mb-2 group-hover:text-[#EA580C] transition-colors" />
                <h3 className="font-semibold text-[#0A192F] text-[11px] mb-0.5 group-hover:text-[#EA580C] transition-colors leading-tight">
                  {cat.name}
                </h3>
                <p className="text-[10px] text-slate-400">{cat.count}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FEATURED PRODUCTS ===================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-2">Top Picks</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">Featured Products</h2>
            </div>
            <Button
              onClick={() => navigate('/products')}
              variant="outline"
              className="hidden sm:flex border-slate-300 text-slate-700 rounded-sm"
              data-testid="view-all-products-btn"
            >
              View All Products
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white border border-slate-100 overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-4"><div className="h-4 bg-slate-200 rounded w-3/4 mb-2" /><div className="h-3 bg-slate-200 rounded w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {featuredProducts.slice(0, 8).map((product) => (
                <Link
                  key={product.product_id}
                  to={`/products/${product.product_id}`}
                  className="group bg-white border border-slate-200 overflow-hidden hover:border-[#0A192F]/20 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                  data-testid={`product-card-${product.product_id}`}
                >
                  <div className="h-44 bg-slate-100 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-slate-300" /></div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-[#0A192F] text-sm line-clamp-2 mb-2 group-hover:text-[#EA580C] transition-colors leading-snug">{product.title}</h3>
                    <div className="text-[#EA580C] font-bold text-sm mb-2">${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)}</div>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>MOQ: {product.moq} {product.moq_unit}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{product.origin_country}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===================== VERIFIED SUPPLIERS ===================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-2">Trusted Partners</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">Verified Suppliers</h2>
            </div>
            <Button
              onClick={() => navigate('/suppliers')}
              variant="outline"
              className="hidden sm:flex border-slate-300 text-slate-700 rounded-sm"
              data-testid="view-all-suppliers-btn"
            >
              View All Suppliers
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="relative">
            {/* Blur overlay for non-paid users */}
            {!isPaidUser(user) && featuredSuppliers.length > 0 && (
              <div className="absolute inset-0 z-10 backdrop-blur-md bg-white/60 flex flex-col items-center justify-center" data-testid="verified-suppliers-blur-overlay">
                <div className="w-16 h-16 rounded-full bg-[#0A192F]/10 flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-[#0A192F]" />
                </div>
                <h3 className="text-lg font-bold text-[#0A192F] mb-2">Upgrade to View Verified Suppliers</h3>
                <p className="text-sm text-slate-500 mb-5 max-w-md text-center">Verified supplier profiles, contact details, and trust ratings are available exclusively for premium members.</p>
                <Button
                  onClick={() => navigate('/membership')}
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-11 px-8 font-semibold"
                  data-testid="verified-suppliers-upgrade-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white border border-slate-100 p-6 animate-pulse">
                    <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-1/2 mx-auto" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {featuredSuppliers.map((supplier) => (
                  <Link
                    key={supplier.user_id}
                    to={`/suppliers/${supplier.user_id}`}
                    className="group bg-white border border-slate-200 p-6 text-center hover:border-[#0A192F]/20 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
                    data-testid={`supplier-card-${supplier.user_id}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#0A192F] flex items-center justify-center mx-auto mb-3 text-white text-lg font-bold group-hover:bg-[#EA580C] transition-colors">
                      {supplier.company_name?.charAt(0) || 'S'}
                    </div>
                    <h3 className="font-semibold text-[#0A192F] text-sm mb-1 group-hover:text-[#EA580C] transition-colors">{supplier.company_name}</h3>
                    <p className="text-xs text-slate-400 mb-2 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />{supplier.country}</p>
                    <div className="flex items-center justify-center gap-1.5 mb-2">
                      {supplier.is_verified && (
                        <span className="badge-verified text-[10px]"><CheckCircle className="w-3 h-3 mr-0.5" />Verified</span>
                      )}
                      {supplier.membership && supplier.membership !== 'free' && (
                        <span className="badge-gold text-[10px]"><Star className="w-3 h-3 mr-0.5" />{supplier.membership}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {supplier.stats?.products || 0} Products &middot; {supplier.stats?.response_rate || 0}% Response
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===================== MEMBERSHIP CTA ===================== */}
      <section className="py-14 lg:py-20 bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#EA580C] mb-3">Premium Membership</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">
                Unlock Your Full Trade Potential
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Upgrade to a premium membership and get verified supplier badges, priority RFQ access,
                unlimited buyer contacts, featured product listings, and dedicated account manager.
              </p>
              <ul className="space-y-2 mb-8">
                {['View buyer contact details', 'Send unlimited quotes', 'Get verified badge', 'Priority in search results', 'Dedicated account manager (Gold+)'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/membership')}
                className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-12 px-8 font-semibold"
                data-testid="cta-membership-btn"
              >
                View Plans & Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { tier: 'Silver', price: '$99/yr', trial: '7-day trial', features: ['50 products', 'Send quotes', 'View contacts'] },
                { tier: 'Gold', price: '$299/yr', trial: '14-day trial', features: ['Unlimited products', 'Verified badge', 'Account manager'] },
              ].map((plan) => (
                <div key={plan.tier} className="bg-white/5 border border-white/10 p-6 hover:bg-white/8 transition-colors">
                  <h4 className="text-[#EA580C] font-bold text-sm mb-0.5">{plan.tier}</h4>
                  <p className="text-white text-2xl font-bold mb-1 tracking-tight">{plan.price}</p>
                  <p className="text-xs text-green-400 font-medium mb-4">{plan.trial}</p>
                  <ul className="space-y-2">
                    {plan.features.map((f, i) => (
                      <li key={i} className="text-xs text-slate-400 flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="py-14 lg:py-20 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto mb-8">
            Join thousands of successful businesses on EaglesMarket.
            Whether you're buying or selling, start trading globally today.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate('/rfqs/create')}
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-12 px-8 font-semibold text-base"
              data-testid="cta-buyer-btn"
            >
              Post Buy Requirement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/register?type=supplier')}
              className="bg-[#0A192F] hover:bg-[#172A46] text-white rounded-sm h-12 px-8 font-semibold text-base"
              data-testid="cta-supplier-btn"
            >
              Start Selling
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* ===================== BUYER ACTION PROMPT POPUP ===================== */}
      {showBuyerPrompt && !isAuthenticated && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up" data-testid="buyer-action-popup">
          <div className="bg-white border border-slate-200 shadow-2xl w-80 overflow-hidden">
            <div className="bg-[#0A192F] px-5 py-3 flex items-center justify-between">
              <h4 className="text-white font-semibold text-sm">Looking for products?</h4>
              <button onClick={() => setShowBuyerPrompt(false)} className="text-white/60 hover:text-white" data-testid="buyer-popup-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600 mb-4">Post your buy requirement in under 60 seconds and receive competitive quotes from verified suppliers.</p>
              <Button
                onClick={() => navigate('/register?type=buyer')}
                className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-10 font-semibold text-sm"
                data-testid="buyer-popup-cta"
              >
                <FileText className="w-4 h-4 mr-2" />
                Post Your Requirement
              </Button>
              <button
                onClick={() => setShowBuyerPrompt(false)}
                className="w-full text-center text-xs text-slate-400 mt-2 hover:text-slate-600"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
